<template>
  <div>
    <v-stepper
      :model-value="step"
      class="bg-transparent"
      flat
      editable
      hide-actions
      mobile-breakpoint="md"
      @update:model-value="goTo"
    >
      <v-stepper-header class="shadow-none">
        <template v-for="(item, index) in steps" :key="item.value">
          <v-divider v-if="index > 0" />
          <v-stepper-item
            :value="item.value"
            :title="item.title"
            :complete="item.complete"
            :error="item.missing"
            :subtitle="item.missing ? t('treezorOnboarding.steps.missingInformation') : undefined"
            :color="item.complete ? 'success' : 'primary'"
          />
        </template>
      </v-stepper-header>

      <!-- Padding keeps step card shadows inside the window's overflow clip. -->
      <v-stepper-window class="mx-0 mb-0 p-1">
        <v-stepper-window-item :value="1">
          <CompanyInformationStep :show-missing="leftSteps.has(1)" @next="onSavedNext(1, $event)" />
        </v-stepper-window-item>
        <v-stepper-window-item :value="2">
          <AccountCreatorStep
            :show-missing="leftSteps.has(2)"
            @previous="goTo(1)"
            @next="onSavedNext(2, $event)"
          />
        </v-stepper-window-item>
        <v-stepper-window-item :value="3">
          <IdentityVerificationStep
            :show-missing="leftSteps.has(3)"
            @previous="goTo(2)"
            @next="goTo(4)"
          />
        </v-stepper-window-item>
        <v-stepper-window-item :value="4">
          <EffectiveBeneficiariesStep
            :show-missing="leftSteps.has(4)"
            @previous="goTo(3)"
            @next="goTo(5)"
          />
        </v-stepper-window-item>
        <v-stepper-window-item :value="5">
          <FileStep
            :show-missing="leftSteps.has(5)"
            @previous="goTo(4)"
            @open-account="steps.forEach((item) => leftSteps.add(item.value))"
          />
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>

    <v-dialog v-model="showReviewDialog" width="400" persistent>
      <v-card>
        <v-card-title class="flex items-center">
          <span>{{ t("treezorOnboarding.documents.reviewConfirmTitle") }}</span>
          <v-spacer />
          <v-btn
            icon="mdi-close"
            :aria-label="t('common.close')"
            variant="text"
            size="small"
            :disabled="!!reviewingDocumentId"
            @click="showReviewDialog = false"
          />
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            {{
              t("treezorOnboarding.documents.reviewConfirmMessage", {
                name: documentToReview
                  ? t(`treezorOnboarding.documents.${documentToReview.labelKey}`)
                  : "",
              })
            }}
          </p>
          <v-form :disabled="!!reviewingDocumentId" @submit.prevent>
            <v-textarea
              v-model.trim="reviewComment"
              :label="t('treezorOnboarding.documents.reviewComment')"
              auto-grow
              rows="2"
            />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-btn :disabled="!!reviewingDocumentId" @click="showReviewDialog = false">
            {{ t("common.close") }}
          </v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            color="success"
            :loading="reviewingStatus === 'VALIDATED'"
            :disabled="!!reviewingDocumentId"
            @click="onConfirmReviewDocument('VALIDATED')"
          >
            {{ t("treezorOnboarding.documents.validate") }}
          </v-btn>
          <v-btn
            variant="flat"
            color="error"
            :loading="reviewingStatus === 'REFUSED'"
            :disabled="!!reviewingDocumentId"
            @click="onConfirmReviewDocument('REFUSED')"
          >
            {{ t("treezorOnboarding.documents.refuse") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteDialog" width="400" :persistent="!!deletingDocumentId">
      <v-card>
        <v-card-title class="flex items-center">
          <span>{{ t("treezorOnboarding.documents.deleteConfirmTitle") }}</span>
          <v-spacer />
          <v-btn
            icon="mdi-close"
            :aria-label="t('common.close')"
            variant="text"
            size="small"
            :disabled="!!deletingDocumentId"
            @click="showDeleteDialog = false"
          />
        </v-card-title>
        <v-card-text>
          {{
            t("treezorOnboarding.documents.deleteConfirmMessage", {
              name: documentToDelete
                ? t(`treezorOnboarding.documents.${documentToDelete.labelKey}`)
                : "",
            })
          }}
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-btn :disabled="!!deletingDocumentId" @click="showDeleteDialog = false">
            {{ t("common.close") }}
          </v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            color="error"
            :loading="!!deletingDocumentId"
            :disabled="!!deletingDocumentId"
            @click="onConfirmDeleteDocument"
          >
            {{ t("treezorOnboarding.documents.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useUserStore } from "@/store";
import { computed, ref, toRef, watch } from "vue";
import AccountCreatorStep from "./steps/AccountCreatorStep.vue";
import CompanyInformationStep from "./steps/CompanyInformationStep.vue";
import EffectiveBeneficiariesStep from "./steps/EffectiveBeneficiariesStep.vue";
import FileStep from "./steps/FileStep.vue";
import IdentityVerificationStep from "./steps/IdentityVerificationStep.vue";
import { provideTreezorOnboarding } from "./useTreezorOnboarding";

const props = defineProps<{
  accountId: string;
}>();

const { t } = useI18n();
const user = useUserStore();
const {
  loadingAccount,
  loadingDocuments,
  loadingEffectiveBeneficiaries,
  loadingAccountCreator,
  companyInfoComplete,
  accountCreatorComplete,
  identityVerificationComplete,
  effectiveBeneficiariesComplete,
  checkedBeneficiaryIncomplete,
  fileComplete,
  showDeleteDialog,
  documentToDelete,
  showReviewDialog,
  documentToReview,
  reviewComment,
  deletingDocumentId,
  reviewingDocumentId,
  reviewingStatus,
  onConfirmReviewDocument,
  onConfirmDeleteDocument,
} = provideTreezorOnboarding(toRef(props, "accountId"));

const step = ref(1);
const leftSteps = ref(new Set<number>());

const steps = computed(() => {
  // Until every load resolves, a step's completeness reflects empty data, not the account.
  const loading =
    loadingAccount.value ||
    loadingDocuments.value ||
    loadingEffectiveBeneficiaries.value ||
    loadingAccountCreator.value;
  return [
    {
      value: 1,
      title: t("treezorOnboarding.steps.company"),
      complete: companyInfoComplete.value,
      flagged: false,
    },
    {
      value: 2,
      title: user.isKind("admin")
        ? t("treezorOnboarding.steps.holder")
        : t("treezorOnboarding.steps.you"),
      complete: accountCreatorComplete.value,
      flagged: false,
    },
    {
      value: 3,
      title: t("treezorOnboarding.steps.identity"),
      complete: identityVerificationComplete.value,
      flagged: false,
    },
    {
      value: 4,
      title: t("treezorOnboarding.steps.beneficiaries"),
      complete: effectiveBeneficiariesComplete.value,
      // A red beneficiary chip flags the step too, before leaving it.
      flagged: checkedBeneficiaryIncomplete.value,
    },
    {
      value: 5,
      title: t("treezorOnboarding.steps.file"),
      complete: fileComplete.value,
      flagged: false,
    },
  ].map((item) => ({
    ...item,
    missing: item.flagged || (!loading && leftSteps.value.has(item.value) && !item.complete),
  }));
});

const goTo = (target: number) => {
  leftSteps.value.add(step.value);
  for (let value = 1; value < target; value++) leftSteps.value.add(value);
  step.value = target;
};
watch(
  () => props.accountId,
  () => {
    step.value = 1;
    leftSteps.value.clear();
  },
);
// Opening stays mounted across accounts, so a save can resolve after switching to another one.
const onSavedNext = (savedStep: number, savedAccountId: string) => {
  if (savedAccountId === props.accountId && step.value === savedStep) goTo(savedStep + 1);
};
</script>
